/**
 * Change videos (D-016, docs/SYSTEM.md → Change videos): which change-log
 * entry has a video, and which is waiting for one.
 *
 * There is no index to keep. The bucket's own listing is the record: every
 * video is stored as `change-videos/<entry slug>-<8 hex of its hash>.mp4`
 * (with a `.jpg` poster and `.vtt` captions beside it), so the key says which
 * entry it belongs to. A video uploaded is a video shown — no commit, no
 * deploy, and nothing that could disagree with what is really stored.
 */
import { z } from 'zod'

export const CHANGE_VIDEO_PREFIX = 'change-videos/'

/**
 * Videos go forward only: the first change to go live after this feature
 * (0069, so entries from 0070) is the first owed one. Earlier entries are
 * never back-filled. A project sets its own number — the first entry it wants
 * a video for — so the template's history never shows "Video coming soon".
 */
export const CHANGE_VIDEOS_FROM = 70

const KEY = /^change-videos\/(\d+-[a-z0-9-]+)-([0-9a-f]{8})\.mp4$/

/** `change-videos/0050-example-change-6851c099.mp4` → its entry's slug, or null. */
export function videoSlug(key: string): string | null {
  return KEY.exec(key)?.[1] ?? null
}

/**
 * `…-6851c099.mp4` → `6851c099`: which upload this is. The page puts it in
 * every address it asks for (`?v=`), because the server lets browsers keep a
 * video's parts for a week — a re-made video must be a new address, or a
 * browser that saw the old one keeps showing it.
 */
export function videoVersion(key: string): string | null {
  return KEY.exec(key)?.[2] ?? null
}

export function changeVideoKey(slug: string, hash: string): string {
  return `${CHANGE_VIDEO_PREFIX}${slug}-${hash.slice(0, 8)}.mp4`
}

/**
 * The objects one video is made of: the film, its poster frame, its captions,
 * and the poster with a play button drawn in — the picture in an "Email
 * update", where a mail app can't play the film itself.
 */
export function changeVideoParts(key: string): {
  video: string
  poster: string
  captions: string
  play: string
} {
  const base = key.replace(/\.mp4$/, '')
  return { video: key, poster: `${base}.jpg`, captions: `${base}.vtt`, play: `${base}-play.jpg` }
}

/**
 * Each entry's video from a bucket listing: the newest upload wins, so a
 * re-made video replaces the old one without deleting anything.
 */
export function latestVideos(
  objects: readonly { key: string; lastModified: string }[],
): Map<string, string> {
  const newest = new Map<string, { key: string; at: string }>()
  for (const object of objects) {
    const slug = videoSlug(object.key)
    if (!slug) continue
    const seen = newest.get(slug)
    if (!seen || object.lastModified > seen.at) {
      newest.set(slug, { key: object.key, at: object.lastModified })
    }
  }
  return new Map([...newest].map(([slug, { key }]) => [slug, key]))
}

/**
 * What an entry shows: its video whenever one exists — whatever its number,
 * so an example kept from before the cutover always plays — else
 * "Video coming soon" when it is owed one, else nothing.
 */
export function videoDisplay(
  entry: { order: number; slug: string },
  live: boolean,
  videos: ReadonlySet<string>,
): 'player' | 'coming-soon' | 'none' {
  if (videos.has(entry.slug)) return 'player'
  return videoDue(entry, live, videos) ? 'coming-soon' : 'none'
}

/** Owed a video: from the cutover on, live, and without one yet. */
export function videoDue(
  entry: { order: number; slug: string },
  live: boolean,
  videos: ReadonlySet<string>,
): boolean {
  return entry.order >= CHANGE_VIDEOS_FROM && live && !videos.has(entry.slug)
}

/** `GET /api/changes/videos`: the entries that have a video, or that the bucket can't be read. */
export const changeVideosResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    videos: z.array(z.string()),
    /** Each entry's current upload (`videoVersion`), for the addresses the page asks for. */
    versions: z.record(z.string(), z.string()).default({}),
  }),
  z.object({ status: z.literal('unavailable') }),
])
export type ChangeVideosResponse = z.infer<typeof changeVideosResponseSchema>

/**
 * The commit the video job checks out to film an entry: its `filmAt:` when
 * set, else the commit it shipped in. `filmAt` exists for an entry whose own
 * commit can't show it — the golden records it needs, or a renderer that can
 * photograph it, came later (docs/SYSTEM.md → The video job).
 */
export function filmCommit(entry: { commit?: string; filmAt?: string }): string | null {
  return entry.filmAt ?? entry.commit ?? null
}
